import { Signal } from '@crown/signal';
import { hasGlobal, isRecord, isString } from '../ir/guards';
import { isJsonObject, isJsonValue, jsonProblem, parseJson } from '../ir/json';
import type { JsonValue } from '../ir/types';
import { InstructionError } from '../ir/values';

/**
 * Data a component fetches, fetched once wherever the page is first rendered.
 *
 * On the server a resource is fetched while the page renders and the render
 * waits for it (or streams it, inside `@try/@pending`); its value goes into
 * the page's payload. In the browser the same resource finds that value and
 * does not fetch again. After that it is a signal: a change to its key
 * fetches again, and the previous fetch is aborted.
 */

export type ResourceState<T> =
  | { readonly state: 'pending' }
  | { readonly state: 'ready'; readonly value: T }
  | { readonly state: 'error'; readonly error: string };

export interface Resource<T> {
  /** The state, tracked: pending, ready with the value, or failed with a message. */
  get(): ResourceState<T>;
  /** The value when ready, `undefined` while pending; a failure is thrown, for the nearest boundary. */
  value(): T | undefined;
  /** The value when ready, `undefined` while pending or failed; never throws, for code that shows a failure itself. */
  ready(): T | undefined;
  /** Whether a fetch is in flight. */
  loading(): boolean;
  /** Fetches the current key again. */
  refetch(): void;
}

export interface FetchOptions {
  readonly signal: AbortSignal;
}

/** A resource value as the page payload carries it. */
export type SerialisedResource =
  | { readonly state: 'ready'; readonly value: JsonValue }
  | { readonly state: 'error'; readonly error: string };

/** The resource values a page payload carries, by key. */
export interface ResourcePayload {
  [key: string]: SerialisedResource;
}

/** Thrown by `value()` when the fetch failed. */
export class ResourceError extends Error {
  override readonly name = 'ResourceError';
  constructor(
    message: string,
    readonly key: string,
  ) {
    super(message);
  }
}

class Entry {
  readonly state: Signal.State<ResourceState<unknown>>;
  controller = new AbortController();
  promise: Promise<void> | null = null;
  /** Which fetch may write: a refetch starts a new one, and an older fetch that lands after it writes nothing. */
  generation = 0;
  refs = 0;
  /**
   * Nobody has read the value since the last page let it go. It is still
   * shown at once to the next page that asks, which fetches it again behind
   * it; a value the server seeded is fresh until it is first read.
   */
  stale = false;
  /** Whether a fetch is in flight, as a signal, so a spinner follows a refetch. */
  readonly busy = new Signal.State(false);

  constructor(initial: ResourceState<unknown>) {
    this.state = new Signal.State(initial, {
      // The last binding reading the value has gone: the page that showed it
      // is disposed, so the next page to ask fetches it again behind it.
      [Signal.subtle.unwatched]: () => {
        if (!this.promise) this.stale = true;
      },
    });
  }
}

/**
 * Every resource one render (on the server) or one page (in the browser)
 * has asked for, by key. A key asked for twice is fetched once.
 */
export class ResourceRegistry {
  private readonly entries = new Map<string, Entry>();
  private readonly pendingReads = new Set<Promise<void>>();
  private readonly written = new Set<string>();
  /** The page this registry renders, as `routeData` keys it: pathname and query. Null in the browser, which follows navigation. */
  path: string | null = null;

  constructor(
    private readonly abortWith: AbortSignal | null = null,
    seed: Readonly<Record<string, SerialisedResource>> | null = null,
    /** Looks for values that arrived since, before a key is fetched: a streamed swap brings its own. */
    private readonly onMiss:
      | ((registry: ResourceRegistry) => void)
      | null = null,
  ) {
    if (seed)
      for (const [key, value] of Object.entries(seed))
        this.entries.set(key, new Entry(value));
  }

  /** Files a value fetched some other way — a route loader the page ran — under its key. */
  put(key: string, state: SerialisedResource): void {
    const entry = this.entries.get(key);
    if (entry) {
      if (entry.promise) entry.controller.abort();
      entry.generation++;
      entry.promise = null;
      entry.state.set(state);
      return;
    }
    this.entries.set(key, new Entry(state));
  }

  /** Adds values the server wrote after this registry was made; only a key not held yet is taken. */
  absorb(seed: Readonly<Record<string, SerialisedResource>>): void {
    for (const [key, value] of Object.entries(seed))
      if (!this.entries.has(key)) this.entries.set(key, new Entry(value));
  }

  /** The entry for `key`, fetching it with `start` when there is none. */
  entry<T>(key: string, start: (signal: AbortSignal) => Promise<T>): Entry {
    let entry = this.entries.get(key);
    if (!entry && this.onMiss) {
      this.onMiss(this);
      entry = this.entries.get(key);
    }
    if (!entry) {
      entry = new Entry({ state: 'pending' });
      this.entries.set(key, entry);
      this.fetch(key, entry, start);
    } else if (entry.stale) {
      // Shown as it was, and fetched again behind it.
      entry.stale = false;
      entry.controller = new AbortController();
      this.fetch(key, entry, start);
    }
    return entry;
  }

  /** Replaces the entry for `key` with a fresh fetch. */
  refresh<T>(key: string, start: (signal: AbortSignal) => Promise<T>): void {
    const entry = this.entries.get(key);
    if (!entry) {
      this.entry(key, start);
      return;
    }
    // The same entry, so everything reading it sees the new value; the value
    // it holds stays shown while the new one is fetched.
    if (entry.promise) entry.controller.abort();
    entry.controller = new AbortController();
    this.fetch(key, entry, start);
  }

  private fetch<T>(
    key: string,
    entry: Entry,
    start: (signal: AbortSignal) => Promise<T>,
  ): void {
    const { signal } = entry.controller;
    const generation = ++entry.generation;
    Signal.subtle.untrack(() => entry.busy.set(true));
    const onAbort = (): void => entry.controller.abort();
    this.abortWith?.addEventListener('abort', onAbort, { once: true });
    entry.promise = (async () => {
      let next: ResourceState<unknown>;
      try {
        next = { state: 'ready', value: await start(signal) };
      } catch (error) {
        next = {
          state: 'error',
          error: error instanceof Error ? error.message : String(error),
        };
      } finally {
        this.abortWith?.removeEventListener('abort', onAbort);
      }
      // Only the latest fetch of the entry still registered for its key
      // writes: one that was replaced or aborted lands nowhere.
      if (entry.generation !== generation) return;
      entry.promise = null;
      entry.busy.set(false);
      if (this.entries.get(key) === entry && !signal.aborted)
        entry.state.set(next);
    })();
  }

  /** Records that a render read `entry` before it settled. */
  notePending(entry: Entry): void {
    if (entry.promise) this.pendingReads.add(entry.promise);
  }

  /** The fetches a render read while pending, cleared; a server render waits for them and renders again. */
  takePendingReads(): Promise<void>[] {
    const reads = [...this.pendingReads];
    this.pendingReads.clear();
    return reads;
  }

  acquire(entry: Entry): void {
    entry.refs++;
  }

  /**
   * Lets go of an entry. One nobody holds is dropped if it is still fetching,
   * and otherwise kept as stale: the next page to ask shows it and fetches it
   * again, so data changed in the meantime is not shown for long.
   */
  release(key: string, entry: Entry): void {
    if (--entry.refs > 0) return;
    if (this.entries.get(key) !== entry) return;
    if (entry.promise) {
      entry.controller.abort();
      this.entries.delete(key);
      return;
    }
    entry.stale = true;
  }

  /** The settled values not handed over yet, marked as handed over. */
  serialiseNew(): ResourcePayload {
    const all = this.serialise();
    for (const key of Object.keys(all)) {
      if (this.written.has(key)) delete all[key];
      else this.written.add(key);
    }
    return all;
  }

  /** The values that loaded, for the page payload. A value that is not JSON is an error naming its key. */
  serialise(): ResourcePayload {
    const out: ResourcePayload = {};
    for (const [key, entry] of this.entries) {
      const state = Signal.subtle.untrack(() => entry.state.get());
      /*
        A failure stays on the server. The page it rendered shows it, but the
        browser asks again for itself: a server fetch fails for reasons the
        browser does not share - no credentials for a signed-out render, a
        service the server cannot reach - and a failure handed over was
        trusted, so the page kept saying "Not authenticated" to somebody who
        was signed in.
      */
      if (state.state !== 'ready') continue;
      if (!isJsonValue(state.value)) {
        throw new InstructionError(
          `The resource ${key} resolved to a value that is not JSON (${jsonProblem(state.value) ?? 'unreadable'}), so it cannot reach the browser`,
        );
      }
      out[key] = { state: 'ready', value: state.value };
    }
    return out;
  }
}

/** A key as the registry files it: canonical JSON, object keys sorted. */
export function resourceKey<K>(key: K): string {
  if (!isJsonValue(key)) {
    let shown: string;
    try {
      shown = String(key);
    } catch {
      shown = 'a value with no text form';
    }
    throw new InstructionError(
      `A resource key must be JSON (strings, numbers, booleans, null, arrays, plain objects); got ${shown}`,
    );
  }
  // The key is JSON, so every value the replacer meets is too.
  return JSON.stringify(key, (_name, value: JsonValue) =>
    isRecord(value)
      ? Object.fromEntries(
          Object.entries(value).sort(([a], [b]) =>
            a < b ? -1 : a > b ? 1 : 0,
          ),
        )
      : value,
  );
}

/**
 * Where a resource files itself right now: the render context's registry on
 * the server (set by the server renderer), the page's in the browser.
 */
const ACTIVE = Symbol.for('crown.resources.active');
const holder: typeof globalThis & { [ACTIVE]?: () => ResourceRegistry | null } =
  globalThis;

/** Called by the server context module, so resources find the request's registry. */
export function setActiveRegistry(find: () => ResourceRegistry | null): void {
  holder[ACTIVE] = find;
}

let pageRegistry: ResourceRegistry | null = null;

function isSerialisedResource(value: JsonValue): value is SerialisedResource {
  if (!isJsonObject(value)) return false;
  if (value.state === 'ready') return 'value' in value;
  return value.state === 'error' && isString(value.error);
}

/** Resource values as the server writes them; anything else carries nothing to use. */
function isResourcePayload(value: JsonValue): value is ResourcePayload {
  return (
    isJsonObject(value) && Object.values(value).every(isSerialisedResource)
  );
}

/** The page payload the server wrote, if this is a server-rendered page. */
function payloadSeed(): ResourcePayload | null {
  if (!hasGlobal('document')) return null;
  const script = document.getElementById('c-state');
  if (!script?.textContent) return null;
  const payload = parseJson(script.textContent);
  if (!payload.ok || !isJsonObject(payload.value)) return null;
  const { resources } = payload.value;
  return resources !== undefined && isResourcePayload(resources)
    ? resources
    : null;
}

/** Takes in every resource script the server wrote that has not been read yet. */
function absorbScripts(registry: ResourceRegistry): void {
  if (!hasGlobal('document')) return;
  for (const script of document.querySelectorAll(
    'script[data-c-resources]:not([data-c-read])',
  )) {
    script.setAttribute('data-c-read', '');
    // A script that does not parse carries nothing to use; the key is fetched.
    const seed = parseJson(script.textContent ?? '{}');
    if (seed.ok && isResourcePayload(seed.value)) registry.absorb(seed.value);
  }
}

/** The browser's registry, seeded from the page payload and the resource scripts the server wrote. */
export function browserRegistry(): ResourceRegistry {
  if (!pageRegistry) {
    pageRegistry = new ResourceRegistry(null, payloadSeed(), absorbScripts);
    absorbScripts(pageRegistry);
  }
  return pageRegistry;
}

/**
 * The value the server's render filed under `key`, from the resource scripts
 * it wrote into the page; undefined when it filed none. For a page that reads one value once, as
 * it starts, and has no use for a resource that fetches and follows it.
 */
export function seededValue<K>(key: K): JsonValue | undefined {
  if (!hasGlobal('document')) return undefined;
  const text = resourceKey(key);
  for (const script of document.querySelectorAll('script[data-c-resources]')) {
    const seed = parseJson(script.textContent ?? '{}');
    const found =
      seed.ok && isJsonObject(seed.value) ? (seed.value[text] ?? null) : null;
    if (isJsonObject(found) && found.state === 'ready') return found.value;
  }
  return undefined;
}

/** Seeds the browser registry by hand, for a page that carries its payload elsewhere. Replaces any earlier registry. */
export function seedResources(
  seed: Readonly<Record<string, SerialisedResource>>,
): void {
  pageRegistry = new ResourceRegistry(null, seed, absorbScripts);
}

export function activeRegistry(): ResourceRegistry {
  return holder[ACTIVE]?.() ?? browserRegistry();
}

export function resource<K, T>(
  key: () => K,
  fetcher: (key: K, options: FetchOptions) => Promise<T>,
): Resource<T> {
  let held: { registry: ResourceRegistry; key: string; entry: Entry } | null =
    null;
  let lastKey: { value: K; text: string } | null = null;

  const current = (): {
    registry: ResourceRegistry;
    key: string;
    entry: Entry;
  } => {
    const value = key();
    const text = resourceKey(value);
    lastKey = { value, text };
    const registry = activeRegistry();
    const entry = registry.entry(text, (signal) => fetcher(value, { signal }));
    if (!held || held.entry !== entry) {
      registry.acquire(entry);
      if (held) held.registry.release(held.key, held.entry);
      held = { registry, key: text, entry };
    }
    return held;
  };

  const get = (): ResourceState<T> => {
    const { registry, entry } = current();
    // SAFETY: an entry is only ever filled by this key's fetcher, which yields T.
    const state = entry.state.get() as ResourceState<T>;
    if (state.state === 'pending') registry.notePending(entry);
    return state;
  };

  const handle: Resource<T> = {
    get,
    value() {
      const state = get();
      if (state.state === 'error')
        throw new ResourceError(state.error, lastKey?.text ?? '');
      return state.state === 'ready' ? state.value : undefined;
    },
    ready() {
      const state = get();
      return state.state === 'ready' ? state.value : undefined;
    },
    loading() {
      return current().entry.busy.get();
    },
    refetch() {
      const known = lastKey ?? { value: key(), text: resourceKey(key()) };
      activeRegistry().refresh(known.text, (signal) =>
        fetcher(known.value, { signal }),
      );
    },
  };
  watchers.set(
    handle,
    () => held !== null && Signal.subtle.hasSinks(held.entry.state),
  );

  return handle;
}

const watchers = new WeakMap<Resource<unknown>, () => boolean>();

/** Whether anything on the page reads the resource now; a resource no page shows has nothing to refresh. */
export function isShown(data: Resource<unknown>): boolean {
  return watchers.get(data)?.() ?? false;
}
